export interface Volume {
  number: string
  title: string
  subtitle: string
  intro: string[]
  questions?: string[]
  highlight?: string
  afterHighlight?: string
  bulletsIntro: string
  bullets: string[]
  sequenceIntro?: string
  sequence?: string
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  resultLead?: string
  resultQuote?: string
  result: string
  resultEmphasis?: string
  popular?: boolean
  popularLabel?: string
}

export const volumes: Volume[] = [
  {
    number: 'Volume 01',
    title: 'Nursing Fundamentals, Patient Care & Essential Clinical Skills',
    subtitle: 'The Foundation Every Nursing Student Needs',
    intro: [
      'Every new cohort starts with the same challenge: hand hygiene, infection control, vital signs, safe patient handling and basic care, all before the first clinical shift.',
      'Fundamentals look simple on paper, but students only get them right when they can see every step and every common error.',
      'This volume turns nursing fundamentals into step-by-step skill sequences, reference ranges, ❌/✅ comparisons, teaching cues and skills-lab drills you can use from the very first class.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'The role of the professional nurse',
      'The nursing process (ADPIE)',
      'Maslow, ABCs & prioritization basics',
      'Hand hygiene: the WHO 5 Moments',
      'Standard & transmission-based precautions',
      'PPE donning & doffing order',
      'Patient identification & fall prevention',
      'Therapeutic communication & teach-back',
      'Vital signs: temperature, pulse & respirations',
      'Blood pressure measurement & common errors',
      'Pulse oximetry & pain assessment',
      'Bed bath, oral care & occupied bed making',
      'Pressure injury prevention & positioning',
      'Body mechanics, transfers & gait belt use',
      'Documentation, charting & SBAR handoff',
      '4-week fundamentals plan & check-off rubric',
    ],
    result: 'Give every student a safe, confident command of the fundamentals before their first clinical rotation.',
  },
  {
    number: 'Volume 02',
    title: 'Human Anatomy, Health Assessment & Clinical Examination',
    subtitle: 'See It, Assess It, Document It',
    intro: [
      'Health assessment is where students feel most unsure: body systems to connect, findings to interpret and a head-to-toe sequence with too many steps to remember.',
      'Without a systematic method, students miss abnormal findings or document them unclearly.',
      'This volume organizes anatomy and assessment into labeled body-system diagrams, step-by-step exam sequences, auscultation maps, scoring scales and peer-practice drills.',
    ],
    popular: true,
    popularLabel: 'CORE ASSESSMENT',
    bulletsIntro: 'Inside:',
    bullets: [
      'Anatomical position, planes & quadrants',
      'Skin & integumentary assessment',
      'Musculoskeletal assessment',
      'Neurological assessment',
      'Glasgow Coma Scale',
      'Cardiovascular system & heart sounds',
      'Peripheral vascular assessment',
      'Respiratory assessment & breath sounds',
      'Abdominal assessment order',
      'Fluids, electrolytes & blood glucose basics',
      'Health history interview',
      'Inspection, palpation, percussion & auscultation',
      'Head-to-toe assessment sequence',
      'Neurovascular checks (the 6 Ps)',
      'Recognizing & documenting abnormal findings',
      'Assessment stations & 4-week plan',
    ],
    result: 'Help students assess every body system and document findings with a clear, repeatable method.',
  },
  {
    number: 'Volume 03',
    title: 'Nursing Procedures, Clinical Interventions & Patient Safety',
    subtitle: 'From Sterile Field to Safe Practice',
    intro: [
      'Nursing procedures demand precision: aseptic technique, the right equipment, the right sequence and constant attention to patient safety.',
      'Explaining a sterile field or an IV setup is hard until students can see every step side by side.',
      'This volume covers core procedures with numbered sequences, equipment charts, ❌/✅ error cards, contamination-spotting drills and task-trainer practice.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Before & after any procedure',
      'Medical vs. surgical asepsis',
      'Setting up a sterile field & sterile gloving',
      'Wound assessment & pressure injury stages',
      'Dry sterile dressing change & wound irrigation',
      'Specimen collection & capillary glucose testing',
      'Indwelling urinary catheter insertion (simulation)',
      'Catheter care & CAUTI prevention',
      'Oxygen therapy & delivery devices',
      'Incentive spirometry & tracheostomy care basics',
      'Peripheral IV therapy & site selection',
      'Priming IV tubing & infusion pumps',
      'IV site assessment, flushing & saline locks',
      'Enteral feeding & ostomy care basics',
      'Sharps safety & isolation room procedures',
      'Procedure stations & check-off rubric',
    ],
    result: 'Help students perform core nursing procedures with precise, safe and consistent technique.',
  },
  {
    number: 'Volume 04',
    title: 'Nursing Pharmacology, Medication Administration & Dosage Calculations',
    subtitle: 'Safe Medications, Correct Calculations',
    intro: [
      'Medication administration is where safety matters most, and dosage calculation is where students are most nervous.',
      'Medication rights, label checks, routes, injection sites and formulas are hard to teach without clear visuals and worked examples.',
      'This volume explains pharmacology and medication safety with labeled diagrams, step-by-step administration sequences, fully worked calculations, sanity checks and practice drills.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Pharmacokinetics & pharmacodynamics',
      'The rights of medication administration',
      'The three label checks',
      'Reading medication orders & drug labels',
      'Abbreviation safety',
      'Routes of administration',
      'Drawing up from a vial & an ampule',
      'Injection angles & intramuscular sites',
      'Subcutaneous injections & insulin basics',
      'Medication classes & high-alert medications',
      'Units & conversions',
      'Desired ÷ Have × Quantity',
      'Weight-based dosing',
      'IV pump rates (mL/h) & drip rates (gtt/min)',
      'Medication error prevention',
      'Medication pass check-off rubric',
    ],
    result: 'Help students give medications safely and calculate every dose correctly.',
  },
  {
    number: 'Volume 05',
    title: 'Medical-Surgical Nursing, Emergency Care & Clinical Decision-Making',
    subtitle: 'Recognize, Prioritize, Respond',
    intro: [
      'Med-surg and emergency care are where students must think fast: who to see first, which signs mean deterioration and when to escalate.',
      'Clinical judgment is hard to teach with lectures alone; students need clear frameworks and realistic cases.',
      'This volume explains med-surg conditions and emergency response with recognition guides, prioritization frameworks, response sequences, unfolding cases and simulation drills.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Clinical reasoning & clinical judgment',
      'Prioritization: who do you see first?',
      'Delegation basics',
      'Early warning signs & rapid response',
      'Heart failure & acute coronary syndrome',
      'COPD, asthma & pneumonia',
      'Diabetes & hypoglycemia response',
      'Stroke & sepsis recognition',
      'Preoperative & postoperative nursing care',
      'Postoperative complications & pain management',
      'Shock & respiratory distress response',
      'Basic Life Support & AED use',
      'Anaphylaxis & seizure care',
      'Neuro checks & triage basics',
      'Clinical decision-making cases',
      'Concept mapping & clinical judgment rubric',
    ],
    result: 'Help students recognize deterioration, set priorities and respond with structure and confidence.',
  },
  {
    number: 'Volume 06',
    title: 'Nursing Instruction, Clinical Simulation & Competency Assessments',
    subtitle: 'Teach With a System',
    intro: [
      'Knowing how to care for patients is not the same as knowing how to teach it.',
      'Nursing instructors need clear methods for lessons, skills demonstrations, simulation, clinical rotations, feedback and competency assessment.',
      'This volume gives you lesson structures, demonstration guides, ready simulation scenarios, debriefing tools, competency checklists, rubrics and program plans ready to use.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'The role of the nursing instructor',
      'Adult learning principles',
      'Learning objectives & Bloom’s taxonomy',
      'Lesson plan template & sample lesson',
      'The 4-step skills demonstration',
      'Return demonstration & coaching',
      'Giving effective feedback',
      'Teaching clinical reasoning',
      'Clinical rotation teaching & conferences',
      'Simulation cycle & prebriefing',
      '5 ready simulation scenarios',
      'Debriefing models & question bank',
      'Competency checklist design',
      'Analytic rubrics & clinical evaluation tools',
      'OSCE station design & remediation',
      '12-week skills lab program',
    ],
    result: 'Plan, deliver and assess every lesson, skills lab, simulation and clinical rotation with structure, from first-semester students to final placements.',
  },
]
